/**
 * AB#2524 — stable identity for the rows of an editable line grid.
 *
 * Line-grid state that has to outlive an edit (which value we last seeded into a row, which row an
 * in-flight lookup was started for) needs a key, and neither obvious candidate works:
 *
 * - **The array index is not stable.** Removing line 1 shifts line 2 into slot 0, so index-keyed state
 *   then describes the wrong row.
 * - **The row object is not stable either.** Editing a cell spreads a new object, and committing maps
 *   rows through `applyComputes`, which returns a fresh object whenever a computed value moved. Keying
 *   on object identity (the AB#2518 attempt) therefore lost its entry on the first ordinary edit.
 *
 * So: allocate a number the first time a row is seen, and CARRY it explicitly onto each replacement.
 * The keys live in a WeakMap, so a row that is dropped takes its entry with it, and they are never
 * written onto the row itself — this is view state, and a synthetic key must not reach the saved
 * document.
 *
 * Keys are unique per grid instance, not globally, and are not meaningful across a remount.
 */
export interface RowKeys {
    /** The row's key, allocating one if this row has not been seen before. */
    keyOf(row: object): number
    /** The row's key if it has one — never allocates. For locating a known key in a live array. */
    peek(row: object): number | undefined
    /** Carries `from`'s key onto `to`, for when an edit replaces a row object. */
    carry(from: object, to: object): void
}

export function createRowKeys(): RowKeys {
    const keys = new WeakMap<object, number>()
    let next = 0

    const keyOf = (row: object): number => {
        let k = keys.get(row)
        if (k === undefined) {
            k = next++
            keys.set(row, k)
        }
        return k
    }

    return {
        keyOf,
        peek: (row: object) => keys.get(row),
        // Allocating on `from` when it has no key yet is deliberate: the caller is mid-replacement and
        // the pair must end up sharing one key either way.
        carry: (from: object, to: object) => {
            if (from !== to) { keys.set(to, keyOf(from)) }
        },
    }
}
